import React, { useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';

export function WonderlandCharacterOverlay() {
  const shouldReduceMotion = useReducedMotion();
  const [magicWandSparks, setMagicWandSparks] = useState(false);
  const [barbieTeaPartyActive, setBarbieTeaPartyActive] = useState(false);

  return (
    <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden">
      {/* Fluttering Fairy Silhouette 1 */}
      <motion.div
        animate={
          shouldReduceMotion
            ? {}
            : {
                x: [0, 80, -40, 0],
                y: [0, -60, -20, 0],
                rotate: [-5, 5, -5],
              }
        }
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-24 left-12 pointer-events-none z-10"
      >
        <div className="relative flex flex-col items-center">
          <svg viewBox="0 0 60 60" className="w-12 h-12 fill-pink-300 drop-shadow-[0_0_15px_rgba(249,168,212,0.9)]">
            <path d="M 30 30 C 10 10 0 25 30 30 C 0 35 10 50 30 30 C 50 10 60 25 30 30 C 60 35 50 50 30 30 Z" />
            <circle cx="30" cy="20" r="6" fill="#ffffff" />
          </svg>
          <span className="text-[10px] font-bold text-pink-300 tracking-wider">PRINCESS FAIRY</span>
        </div>
      </motion.div>

      {/* Floating Magic Unicorn Silhouette */}
      <motion.div
        animate={
          shouldReduceMotion
            ? {}
            : {
                x: [0, -60, 30, 0],
                y: [0, -40, 0],
              }
        }
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-28 right-12 pointer-events-none z-10"
      >
        <div className="flex flex-col items-center">
          <svg viewBox="0 0 80 80" className="w-14 h-14 fill-purple-300 drop-shadow-[0_0_15px_rgba(167,243,208,0.9)]">
            <path d="M 40 10 L 45 30 L 35 30 Z" fill="#ffd700" />
            <circle cx="40" cy="35" r="16" fill="#fbcfe8" />
            <path d="M 40 51 C 25 51 20 70 40 70 C 60 70 55 51 40 51 Z" />
          </svg>
          <span className="text-[10px] font-bold text-emerald-200 tracking-wider">MAGIC UNICORN</span>
        </div>
      </motion.div>

      {/* Barbie Tea Party Celebration Trigger */}
      <motion.div
        animate={shouldReduceMotion ? {} : { scale: [1, 1.05, 1] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-12 left-6 md:left-14 pointer-events-auto z-30"
      >
        <button
          onClick={() => {
            setBarbieTeaPartyActive(true);
            setTimeout(() => setBarbieTeaPartyActive(false), 3500);
          }}
          className="flex items-center gap-2 rounded-2xl border border-pink-300/80 bg-pink-900/90 px-4 py-2 text-xs font-black text-pink-100 backdrop-blur-md transition-all hover:bg-pink-800 cursor-pointer shadow-lg shadow-pink-500/40"
        >
          <span className="text-lg">🫖</span>
          <span>BARBIE TEA PARTY!</span>
        </button>
      </motion.div>

      {/* Barbie Tea Party Pop-up Modal Story */}
      {barbieTeaPartyActive && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 pointer-events-none flex items-center justify-center z-40"
        >
          <div className="rounded-3xl border-4 border-pink-300 bg-pink-950/95 p-8 text-center shadow-2xl shadow-pink-500/60 max-w-md mx-4">
            <span className="text-5xl animate-bounce inline-block mb-3">🫖 🎂 👑 💖</span>
            <h3 className="text-2xl font-black text-pink-200 uppercase tracking-wider">
              BARBIE PRINCESS TEA PARTY & CAKE CUTTING!
            </h3>
            <p className="text-sm font-semibold text-pink-100 mt-2">
              All the fairies and Barbie dolls are singing Happy Birthday under the rainbow cloud!
            </p>
            <div className="mt-4 inline-block rounded-full bg-pink-300 px-6 py-2 text-xs font-black text-pink-950 uppercase">
              ROYAL PRINCESS APPROVED ✨
            </div>
          </div>
        </motion.div>
      )}

      {/* Magic Wand Interactive Button */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 pointer-events-auto z-30">
        <button
          onClick={() => {
            setMagicWandSparks(true);
            setTimeout(() => setMagicWandSparks(false), 3000);
          }}
          className="flex items-center gap-2 rounded-full border border-pink-300 bg-pink-900/90 px-6 py-3 text-sm font-bold text-pink-100 shadow-xl shadow-pink-500/40 backdrop-blur-md transition-transform hover:scale-110 active:scale-95 cursor-pointer"
        >
          <span>🪄</span> CAST BARBIE FAIRY MAGIC WAND
        </button>
      </div>

      {/* Magic Wand Sparkles Burst Overlay */}
      {magicWandSparks && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1.2 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 pointer-events-none flex items-center justify-center z-40"
        >
          <div className="rounded-3xl border-2 border-pink-200 bg-pink-950/95 px-10 py-6 text-center backdrop-blur-xl shadow-2xl shadow-pink-500/60">
            <h3 className="text-3xl font-extrabold text-pink-100 tracking-wider">✨ PRINCESS WISH GRANTED! ✨</h3>
            <p className="text-xs font-bold text-pink-200 mt-2">Balloons, Rainbows, Barbie Doll Magic & Candy Shower Unleashed!</p>
          </div>
        </motion.div>
      )}
    </div>
  );
}
